import { IconFromSet, type IconValue } from "@cmp-src/icons";
import { getIconColorForBackground } from "@gettactic/helpers/src/colors/iconColor";
import { cn } from "@gettactic/helpers/src/css/cn";
import React from "react";

export type IconTileSize = "xs" | "sm" | "md" | "lg";

/**
 * Tile box classes + the IconFromSet `size` unit (1 unit = 4px) per size.
 *
 * `xs` is badge-sized — a nav row, a calendar card — where a 32px tile would
 * set the row height. It gives the glyph a larger share of the box than the
 * other sizes do: below ~20px the ratio that reads as balanced on a big tile
 * leaves too little ink to recognise the icon by.
 */
const SIZE_STYLES: Record<IconTileSize, { box: string; iconUnits: number }> = {
  xs: { box: "h-5 w-5 rounded-md", iconUnits: 3 },
  sm: { box: "h-8 w-8 rounded-lg", iconUnits: 3.5 },
  md: { box: "h-9 w-9 rounded-[10px]", iconUnits: 4 },
  lg: { box: "h-10 w-10 rounded-xl", iconUnits: 5 }
};

/** Background used when no color is provided (matches the category default tint). */
export const ICON_TILE_FALLBACK_COLOR = "#F3F4F6";

export interface IconTileProps {
  /** Background color (hex). The icon color is derived to coordinate with it. */
  color?: string | null;
  /** Icon from a set; rendered with the derived ink color. */
  icon?: IconValue | null;
  /**
   * Custom icon node, used when `icon` is not provided. It inherits the derived
   * ink color via `currentColor`, so icons should not hard-code their own color.
   */
  children?: React.ReactNode;
  /** Override the auto-derived icon color. */
  iconColor?: string;
  size?: IconTileSize;
  /** Subtle hairline border (matches the request/detail tiles). */
  bordered?: boolean;
  className?: string;
}

/**
 * A square tile filled with a background color and a centered icon whose color is
 * automatically derived to stay legible and on-hue with the background (via
 * `getIconColorForBackground`). Use everywhere a colored background pairs with an
 * icon so the pairing stays consistent.
 */
export const IconTile: React.FC<IconTileProps> = ({
  color,
  icon,
  children,
  iconColor,
  size = "md",
  bordered = false,
  className
}) => {
  const background = color || ICON_TILE_FALLBACK_COLOR;
  const ink = iconColor ?? getIconColorForBackground(background);
  const { box, iconUnits } = SIZE_STYLES[size];

  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center",
        box,
        bordered && "border border-black/5",
        className
      )}
      style={{ backgroundColor: background, color: ink }}
    >
      {icon ? (
        <IconFromSet icon={icon} size={iconUnits} color={ink} />
      ) : (
        children
      )}
    </span>
  );
};
